import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService, cursosService } from '../services/api';
import { formatPersonName } from '../utils/personName';
import { formatDni } from '../utils/dni';
import ConfirmPopup from '../components/ConfirmPopup';
import './Estudiantes.css';

const PER_PAGE = 30;

const Estudiantes = () => {
  const navigate = useNavigate();
  const [cursos, setCursos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingResumen, setLoadingResumen] = useState(false);
  const [resumen, setResumen] = useState([]);
  const [selectedCursoId, setSelectedCursoId] = useState('');
  const [mostrarCursosInactivos, setMostrarCursosInactivos] = useState(false);
  const [filterText, setFilterText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [error, setError] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  const [isProfesor, setIsProfesor] = useState(false);
  const [isCoordinador, setIsCoordinador] = useState(false);
  const [exportCsvAlcance, setExportCsvAlcance] = useState('todos'); // 'todos' | 'aprobados'

  // Estado para el modal de agregar estudiante manualmente
  const [showModalAgregar, setShowModalAgregar] = useState(false);
  const [searchCandidatos, setSearchCandidatos] = useState('');
  const [candidatos, setCandidatos] = useState([]);
  const [loadingCandidatos, setLoadingCandidatos] = useState(false);
  const [errorCandidatos, setErrorCandidatos] = useState('');
  const [mensajeExitoModal, setMensajeExitoModal] = useState('');
  const [agregandoUserId, setAgregandoUserId] = useState(null);

  useEffect(() => {
    if (!authService.isAuthenticated()) {
      navigate('/login');
      return;
    }
    const user = authService.getCurrentUser();
    setCurrentUser(user);
    const savedRole = localStorage.getItem('selectedRole');
    if (savedRole) {
      setSelectedRole(savedRole);
      setIsProfesor(savedRole === 'Profesor');
      setIsCoordinador(savedRole === 'Coordinador');
    } else if (user && user.groups) {
      const grupos = Array.isArray(user.groups) ? user.groups : [user.groups];
      const primerRol = typeof grupos[0] === 'string' ? grupos[0] : (grupos[0].name || String(grupos[0]));
      setSelectedRole(primerRol);
      setIsProfesor(primerRol === 'Profesor');
      setIsCoordinador(primerRol === 'Coordinador');
      localStorage.setItem('selectedRole', primerRol);
    }
    loadCursos();
  }, [navigate]);

  useEffect(() => {
    if (currentUser !== null) {
      loadCursos();
    }
  }, [currentUser, isProfesor, isCoordinador]);

  const cursosParaSelector = mostrarCursosInactivos
    ? (cursos || []).filter(c => c.activo === false)
    : (cursos || []).filter(c => c.activo !== false);

  useEffect(() => {
    const list = mostrarCursosInactivos ? (cursos || []).filter(c => c.activo === false) : (cursos || []).filter(c => c.activo !== false);
    if (selectedCursoId && !list.some(c => String(c.id) === String(selectedCursoId))) {
      setSelectedCursoId('');
    }
  }, [mostrarCursosInactivos, cursos, selectedCursoId]);

  useEffect(() => {
    const handleRoleChange = (event) => {
      const newRole = event.detail?.role || localStorage.getItem('selectedRole');
      if (newRole && newRole !== selectedRole) {
        setSelectedRole(newRole);
        setIsProfesor(newRole === 'Profesor');
        setIsCoordinador(newRole === 'Coordinador');
      }
    };
    window.addEventListener('roleChanged', handleRoleChange);
    window.addEventListener('storage', handleRoleChange);
    return () => {
      window.removeEventListener('roleChanged', handleRoleChange);
      window.removeEventListener('storage', handleRoleChange);
    };
  }, [selectedRole]);

  const loadCursos = async () => {
    try {
      const data = await cursosService.getCursos();
      if (isProfesor && !isCoordinador && currentUser) {
        const cursosFiltrados = (data || []).filter(curso => {
          if (curso.equipo && Array.isArray(curso.equipo)) {
            return curso.equipo.some(miembroId => {
              const id = typeof miembroId === 'object' && miembroId.id ? miembroId.id : miembroId;
              return parseInt(id) === parseInt(currentUser.id);
            });
          }
          return false;
        });
        setCursos(cursosFiltrados);
      } else {
        setCursos(data || []);
      }
    } catch (err) {
      const msg = err.response?.data?.error || 'Error al cargar cursos';
      setError(msg);
      setCursos([]);
      console.error('Error al cargar cursos:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!selectedCursoId) {
      setResumen([]);
      return;
    }
    let cancelled = false;
    setLoadingResumen(true);
    setError('');
    cursosService.getResumenEstudiantes(selectedCursoId)
      .then(data => {
        if (!cancelled) setResumen(Array.isArray(data) ? data : (data?.estudiantes ?? []));
      })
      .catch(err => {
        if (!cancelled) {
          setError(err.response?.data?.error || 'Error al cargar resumen de estudiantes');
          setResumen([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingResumen(false);
      });
    return () => { cancelled = true; };
  }, [selectedCursoId]);

  useEffect(() => {
    setFilterText('');
    setCurrentPage(1);
  }, [selectedCursoId]);

  const filterLower = (filterText || '').trim().toLowerCase();
  const filteredResumen = filterLower
    ? (resumen || []).filter(row => {
        const nombre = (row.nombre || '').toLowerCase();
        const email = (row.email || '').toLowerCase();
        return nombre.includes(filterLower) || email.includes(filterLower);
      })
    : (resumen || []);

  const totalPages = Math.max(1, Math.ceil(filteredResumen.length / PER_PAGE));
  const page = Math.min(currentPage, totalPages);
  const start = (page - 1) * PER_PAGE;
  const paginatedResumen = filteredResumen.slice(start, start + PER_PAGE);

  const abrirModalAgregar = () => {
    setShowModalAgregar(true);
    setSearchCandidatos('');
    setErrorCandidatos('');
    setMensajeExitoModal('');
    cargarCandidatos('');
  };

  const cerrarModalAgregar = () => {
    setShowModalAgregar(false);
    setSearchCandidatos('');
    setErrorCandidatos('');
    setMensajeExitoModal('');
    setCandidatos([]);
  };

  const cargarCandidatos = async (q = '') => {
    if (!selectedCursoId) return;
    setLoadingCandidatos(true);
    setErrorCandidatos('');
    try {
      const data = await cursosService.getCandidatosEstudiantes(selectedCursoId, q);
      setCandidatos(data || []);
    } catch (err) {
      setErrorCandidatos(err.response?.data?.error || 'Error al buscar alumnos');
      setCandidatos([]);
    } finally {
      setLoadingCandidatos(false);
    }
  };

  const handleSearchCandidatosChange = (e) => {
    const val = e.target.value;
    setSearchCandidatos(val);
    cargarCandidatos(val);
  };

  const handleAgregarEstudiante = async (candidato) => {
    if (!selectedCursoId || agregandoUserId) return;
    setAgregandoUserId(candidato.id);
    setErrorCandidatos('');
    setMensajeExitoModal('');
    try {
      const res = await cursosService.agregarEstudianteManual(selectedCursoId, candidato.id);
      setMensajeExitoModal(res.message || 'Estudiante inscripto correctamente.');
      setCandidatos(prev => prev.filter(c => c.id !== candidato.id));
      // Actualizar la lista del resumen de estudiantes
      const dataResumen = await cursosService.getResumenEstudiantes(selectedCursoId);
      setResumen(Array.isArray(dataResumen) ? dataResumen : (dataResumen?.estudiantes ?? []));
    } catch (err) {
      setErrorCandidatos(err.response?.data?.error || 'Error al inscribir al estudiante.');
    } finally {
      setAgregandoUserId(null);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [filterText]);

  const escapeCsv = (val) => {
    const s = String(val ?? '');
    if (s.includes(',') || s.includes('"') || s.includes('\n') || s.includes('\r')) {
      return '"' + s.replace(/"/g, '""') + '"';
    }
    return s;
  };

  const exportarCsv = () => {
    const base = filteredResumen;
    const filasExport = exportCsvAlcance === 'aprobados'
      ? base.filter((row) => row.aprobado && !row.dado_de_baja)
      : base;

    if (filasExport.length === 0) {
      setError(
        exportCsvAlcance === 'aprobados'
          ? 'No hay estudiantes aprobados para exportar.'
          : 'No hay estudiantes para exportar.'
      );
      return;
    }

    const cursoNombre = resumen[0]?.curso_nombre || cursos.find(c => String(c.id) === String(selectedCursoId))?.nombre || 'Curso';
    const filas = filasExport.map((row) => {
      const apellido = formatPersonName(row.last_name || '');
      const nombre = formatPersonName(row.first_name || '');
      return [
        escapeCsv(row.curso_nombre || cursoNombre),
        escapeCsv(row.alianza_trayecto || 'Tecno 3F'),
        escapeCsv(apellido),
        escapeCsv(nombre),
        escapeCsv(row.genero ?? ''),
        escapeCsv(row.edad != null && row.edad !== '' ? row.edad : ''),
        escapeCsv(formatDni(row.dni ?? '')),
        escapeCsv(row.localidad ?? ''),
        escapeCsv(row.email ?? ''),
        escapeCsv(row.telefono ?? ''),
      ].join(',');
    });
    const header = 'Curso,Alianza / Trayecto,Apellido,Nombre,Género,Edad,DNI,Localidad,Correo,Teléfono';
    const csv = '\uFEFF' + header + '\r\n' + filas.join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const slugCurso = cursoNombre.replace(/[^a-z0-9]/gi, '-');
    const slugAlcance = exportCsvAlcance === 'aprobados' ? '-aprobados' : '';
    a.download = `estudiantes-${slugCurso}${slugAlcance}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading && cursos.length === 0) {
    return (
      <div className="estudiantes-loading">
        <div className="loading-spinner"></div>
        <p>Cargando...</p>
      </div>
    );
  }

  return (
    <div className="estudiantes-container">
      <div className="estudiantes-header">
        <h1>Resumen de estudiantes</h1>
        <div className="estudiantes-header-actions">
          {selectedCursoId && (
            <button
              type="button"
              className="estudiantes-btn-agregar-alumno"
              onClick={abrirModalAgregar}
              title="Inscribir manualmente un alumno a este curso"
            >
              ➕ Agregar alumno
            </button>
          )}
          {selectedCursoId && filteredResumen.length > 0 && !loadingResumen && (
            <div className="estudiantes-exportar">
              <label htmlFor="estudiantes-export-alcance" className="estudiantes-exportar-label">
                Exportar
              </label>
              <select
                id="estudiantes-export-alcance"
                className="estudiantes-exportar-select"
                value={exportCsvAlcance}
                onChange={(e) => setExportCsvAlcance(e.target.value)}
              >
                <option value="todos">Todos los estudiantes</option>
                <option value="aprobados">Solo aprobados</option>
              </select>
              <button type="button" className="estudiantes-btn-exportar" onClick={exportarCsv}>
                📥 CSV
              </button>
            </div>
          )}
        </div>
      </div>

      {error && (
        <ConfirmPopup
          open
          title="Aviso"
          message={error}
          confirmLabel="Aceptar"
          cancelLabel={null}
          onConfirm={() => setError('')}
          onCancel={() => setError('')}
          variant="danger"
        />
      )}

      <div className="estudiantes-curso-selector">
        <div className="estudiantes-form-group">
          <label htmlFor="selector-curso">Curso</label>
          <select
            id="selector-curso"
            className="estudiantes-select-curso"
            value={selectedCursoId}
            onChange={(e) => setSelectedCursoId(e.target.value || '')}
          >
            <option value="">Seleccionar curso</option>
            {cursosParaSelector.map((curso) => (
              <option key={curso.id} value={curso.id}>
                {curso.nombre}
                {curso.activo === false ? ' (inactivo)' : ''}
              </option>
            ))}
          </select>
        </div>
        <label className="estudiantes-checkbox-label estudiantes-checkbox-inactivos">
          <input
            type="checkbox"
            checked={mostrarCursosInactivos}
            onChange={(e) => setMostrarCursosInactivos(e.target.checked)}
          />
          <span>Mostrar solo cursos inactivos</span>
        </label>
      </div>

      {!selectedCursoId ? (
        <div className="estudiantes-sin-curso">
          <p>Seleccione un curso en el desplegable para ver el resumen de estudiantes.</p>
        </div>
      ) : (
        <>
          {loadingResumen ? (
            <div className="estudiantes-loading-resumen">
              <div className="loading-spinner"></div>
              <p>Cargando resumen...</p>
            </div>
          ) : (
            <>
              <div className="estudiantes-filtro">
                <label htmlFor="estudiantes-filtro-input" className="estudiantes-filtro-label">
                  Filtrar por nombre o email
                </label>
                <input
                  id="estudiantes-filtro-input"
                  type="text"
                  className="estudiantes-filtro-input"
                  placeholder="Escriba nombre o email..."
                  value={filterText}
                  onChange={(e) => setFilterText(e.target.value)}
                />
              </div>
              <div className="estudiantes-table-container">
                <table className="estudiantes-table">
                  <thead>
                    <tr>
                      <th>Estudiante</th>
                      <th>Inasistencias</th>
                      <th>Tareas aprobadas</th>
                      <th>Nota (promedio)</th>
                      <th>Dado de baja</th>
                      <th>Aprobado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedResumen.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="estudiantes-empty">
                          {filterLower ? 'Ningún estudiante coincide con el filtro' : 'No hay estudiantes inscriptos en este curso'}
                        </td>
                      </tr>
                    ) : (
                      paginatedResumen.map((row) => (
                        <tr key={row.id}>
                          <td>
                            <span className="estudiantes-nombre">{row.nombre || row.email || '-'}</span>
                            {row.email && row.nombre && (
                              <span className="estudiantes-email">{row.email}</span>
                            )}
                          </td>
                          <td>{row.inasistencias ?? 0}</td>
                          <td>{row.tareas_entregadas ?? 0}</td>
                          <td>{row.nota != null && row.nota !== '' ? Number(row.nota).toFixed(2) : '—'}</td>
                          <td className="estudiantes-cell-baja">
                            {row.dado_de_baja && row.fecha_baja
                              ? (() => {
                                  const match = String(row.fecha_baja).match(/^(\d{4})-(\d{2})-(\d{2})/);
                                  if (match) {
                                    const [, y, m, d] = match;
                                    return new Date(Number(y), Number(m) - 1, Number(d)).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
                                  }
                                  return row.fecha_baja;
                                })()
                              : '—'}
                          </td>
                          <td className="estudiantes-cell-aprobado">
                            {row.dado_de_baja ? (
                              <span className="estudiantes-aprobado-na">—</span>
                            ) : (
                              <input
                                type="checkbox"
                                checked={!!row.aprobado}
                                onChange={async (e) => {
                                  const nuevo = e.target.checked;
                                  try {
                                    await cursosService.updateEstudianteAprobado(selectedCursoId, row.id, nuevo);
                                    setResumen(prev => prev.map(r => r.id === row.id ? { ...r, aprobado: nuevo } : r));
                                  } catch (err) {
                                    console.error('Error al actualizar aprobado:', err);
                                  }
                                }}
                                title={row.aprobado ? 'Marcar como no aprobado' : 'Marcar como aprobado'}
                                aria-label={row.aprobado ? 'Aprobado' : 'No aprobado'}
                              />
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              {filteredResumen.length > 0 && (
                <div className="estudiantes-paginacion">
                  <button
                    type="button"
                    className="estudiantes-btn-paginacion"
                    disabled={page <= 1}
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  >
                    Anterior
                  </button>
                  <span className="estudiantes-paginacion-info">
                    Mostrando {start + 1}-{Math.min(start + PER_PAGE, filteredResumen.length)} de {filteredResumen.length}
                    {totalPages > 1 && ` · Página ${page} de ${totalPages}`}
                  </span>
                  <button
                    type="button"
                    className="estudiantes-btn-paginacion"
                    disabled={page >= totalPages}
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  >
                    Siguiente
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}

      {showModalAgregar && (
        <div className="estudiantes-modal-overlay" onClick={cerrarModalAgregar}>
          <div className="estudiantes-modal" onClick={(e) => e.stopPropagation()}>
            <div className="estudiantes-modal-header">
              <div>
                <h2>Agregar alumno al curso</h2>
                <div className="estudiantes-modal-subtitulo">
                  {cursos.find(c => String(c.id) === String(selectedCursoId))?.nombre || 'Curso seleccionado'}
                </div>
              </div>
              <button
                type="button"
                className="estudiantes-modal-close"
                onClick={cerrarModalAgregar}
                title="Cerrar modal"
              >
                ×
              </button>
            </div>

            <div className="estudiantes-modal-body">
              <p className="estudiantes-modal-aviso">
                Permite inscribir manualmente a un alumno que no se haya llegado a inscribir. Esta acción asigna al estudiante al curso directamente sin enviar mails de notificación.
              </p>

              <div className="estudiantes-modal-buscador">
                <input
                  type="text"
                  className="estudiantes-modal-input-search"
                  placeholder="Buscar alumno por nombre, apellido, email o DNI..."
                  value={searchCandidatos}
                  onChange={handleSearchCandidatosChange}
                  autoFocus
                />
              </div>

              {mensajeExitoModal && (
                <div className="estudiantes-modal-mensaje-exito">
                  {mensajeExitoModal}
                </div>
              )}

              <ConfirmPopup
                open={Boolean(errorCandidatos)}
                title="Error"
                message={errorCandidatos}
                confirmLabel="Aceptar"
                cancelLabel={null}
                onConfirm={() => setErrorCandidatos('')}
                onCancel={() => setErrorCandidatos('')}
                variant="danger"
              />

              <div className="estudiantes-modal-lista">
                {loadingCandidatos && candidatos.length === 0 ? (
                  <div className="estudiantes-modal-loading">
                    <div className="loading-spinner"></div>
                    <p>Buscando alumnos disponibles...</p>
                  </div>
                ) : candidatos.length === 0 ? (
                  <div className="estudiantes-modal-vacio">
                    {searchCandidatos
                      ? 'No se encontraron alumnos disponibles que coincidan con la búsqueda.'
                      : 'No hay alumnos disponibles para agregar o escriba para buscar.'}
                  </div>
                ) : (
                  candidatos.map((c) => (
                    <div key={c.id} className="estudiantes-candidato-card">
                      <div className="estudiantes-candidato-info">
                        <span className="estudiantes-candidato-nombre">{c.nombre || c.username}</span>
                        <div className="estudiantes-candidato-datos">
                          {c.email && <span className="estudiantes-candidato-email">{c.email}</span>}
                          {c.dni && <span className="estudiantes-candidato-dni">DNI: {formatDni(c.dni)}</span>}
                        </div>
                      </div>
                      <button
                        type="button"
                        className="estudiantes-btn-inscribir-candidato"
                        disabled={agregandoUserId === c.id}
                        onClick={() => handleAgregarEstudiante(c)}
                      >
                        {agregandoUserId === c.id ? 'Asignando...' : '➕ Inscribir'}
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="estudiantes-modal-actions">
              <button
                type="button"
                className="estudiantes-modal-btn-cerrar"
                onClick={cerrarModalAgregar}
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Estudiantes;
